import * as React from 'react'
import {
  List,
  TextInput,
  NumberInput,
  TopToolbar,
  SelectColumnsButton,
  FilterButton,
  DatagridConfigurable,
  TextField,
  DateInput,
  FunctionField,
  Link,
  SelectInput,
  DateField,
  useNotify,
  useGetList,
  useListContext,
} from 'react-admin'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, Grid, Typography, Box } from '@mui/material'
import { labTestResultLabelWithNotes, ResultDetailPdf } from '../ResultList/Fields'
import { ViewTestResultAction } from '../ResultList/Actions'
import Empty from '../../common/Empty'
import StatusPill from '../../common/StatusPill'
import { KitStatusLabels } from '../../../utils/constants'
import { formatDateToMMDDYYYY } from '../../../utils/helpers'

const OrderSummary = ({ orderId }) => {
  const { data, isLoading } = useGetList(`provider-portal/bulk-orders/${orderId}`, {
    filter: {},
  })

  if (isLoading || !data || data.length === 0) {
    return null
  }

  const testedKitStatuses = ['testComplete', 'notifyComplete', 'error', 'manualReview']
  const testedKits = data.filter((kit) => testedKitStatuses.includes(kit.status)).length
  const untestedKits = data.length - testedKits

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Card
        sx={{
          background: 'var(--surface-2)',
          border: '0.5px solid var(--border)',
          borderRadius: '12px',
          boxShadow: 'none',
        }}
      >
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Order #
              </Typography>
              <Typography variant="h6">{data[0].shopifyOrderNumber || orderId}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Order Date
              </Typography>
              <Typography variant="h6">{formatDate(data[0].orderDate)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="body2" color="text.secondary">
                Total Kits
              </Typography>
              <Typography variant="h6">{data.length}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="body2" color="text.secondary">
                Tested Kits
              </Typography>
              <Typography variant="h6" sx={{ color: 'var(--text-success)' }}>
                {testedKits}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="body2" color="text.secondary">
                Not Tested Kits
              </Typography>
              <Typography variant="h6" sx={{ color: 'var(--text-warning)' }}>
                {untestedKits}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

const KitIdLink = ({ record }) => {
  const { data } = useListContext()
  const resultListIds = (data || []).map((kit) => kit.kitId)

  return (
    <Link
      to={`/provider-portal/results/${record.kitId}/show`}
      state={{ resultListIds }}
      style={{ color: 'var(--text-accent)', textDecoration: 'none' }}
    >
      {record.kitId}
    </Link>
  )
}

const KitsAssociatedToBulkOrders = () => {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const notify = useNotify()

  React.useEffect(() => {
    if (!orderId) {
      navigate(-1)
    }
  }, [navigate, orderId])

  const filterInputSx = {
    '& .MuiInputBase-root': { borderRadius: 'var(--radius)' },
  }

  const filterArr = [
    <NumberInput alwaysOn label="KitId" source="kitId" size="small" sx={filterInputSx} style={{ width: '250px' }} />,
    <TextInput
      alwaysOn
      label="Patient Name"
      source="patientName"
      resettable
      size="small"
      sx={filterInputSx}
      style={{ width: '250px' }}
    />,
    <DateInput alwaysOn label="DOB" source="patientDoB" resettable size="small" sx={filterInputSx} />,
    <SelectInput
      alwaysOn
      label="Tested/Not Tested"
      source="showTestedKits"
      choices={[
        { id: 'true', name: 'Tested Kits' },
        { id: 'false', name: 'Not Tested Kits' },
      ]}
      emptyText={'All Kits'}
      resettable
      size="small"
      sx={filterInputSx}
      style={{ width: '250px' }}
    />,
    <SelectInput
      alwaysOn
      label="Result"
      source="result"
      choices={[
        { id: 'none', name: 'Not Tested' },
        { id: 'Positive', name: 'Positive' },
        { id: 'NSS', name: 'NSS' },
        { id: 'error', name: 'Error' },
      ]}
      emptyText={'All Results'}
      resettable
      size="small"
      sx={filterInputSx}
      style={{ width: '250px' }}
    />,
    <SelectInput
      alwaysOn
      label="Acknowledgment"
      source="acknowledgment"
      choices={[
        { id: 'acknowledged', name: 'Acknowledged' },
        { id: 'unacknowledged', name: 'Un-Acknowledged' },
      ]}
      emptyText={'All'}
      resettable
      size="small"
      sx={filterInputSx}
      style={{ width: '250px' }}
    />,
  ]

  // Full-row tint so an unacknowledged result is spottable at a glance while
  // skimming the list, not just by reading the 'Provider Ack'd' column.
  const resultRowStyle = (record) => ({
    backgroundColor: record.providerAcknowledgedAt > 0 ? '#efefef' : '#d9edf7',
  })

  return (
    <List
      resource={`provider-portal/bulk-orders/${orderId}`}
      filter={{ prepaidOrderId: orderId }}
      actions={
        <TopToolbar>
          <Box
            sx={{
              '& .add-filter': {
                color: 'var(--text-accent)',
                background: 'none',
                boxShadow: 'none',
                textTransform: 'none',
              },
              '& .add-filter:hover': { background: 'var(--fill-ghost-hover)' },
            }}
          >
            <FilterButton />
          </Box>
          <SelectColumnsButton sx={{ color: 'var(--text-accent)', textTransform: 'none' }} />
        </TopToolbar>
      }
      filters={filterArr}
      pagination={false}
      empty={<Empty text="No kits found for this order." />}
      sx={{
        '& .RaList-content': {
          background: 'var(--surface-2)',
          border: '0.5px solid var(--border)',
          borderRadius: '12px',
          boxShadow: 'none',
          overflow: 'hidden',
        },
      }}
    >
      <OrderSummary orderId={orderId} />
      <DatagridConfigurable
        bulkActionButtons={false}
        rowStyle={resultRowStyle}
        sx={{
          '& .RaDatagrid-headerCell': {
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--text-secondary)',
            textTransform: 'none',
            borderBottom: '0.5px solid var(--border)',
          },
          '& .RaDatagrid-rowCell': {
            padding: '16px',
            borderRight: 'none',
          },
          '& .RaDatagrid-row': {
            borderBottom: '0.5px solid var(--border)',
          },
          '& .RaDatagrid-row:last-of-type': {
            borderBottom: 'none',
          },
          '& .RaDatagrid-row:hover': {
            backgroundColor: 'var(--fill-ghost-hover)',
          },
        }}
      >
        <FunctionField label="KitId" render={(record) => <KitIdLink record={record} />} />
        <TextField label="shopifyOrderNumber" source="shopifyOrderNumber" />
        <TextField label="Patient Name" source="patientName" />
        <FunctionField render={(record) => formatDateToMMDDYYYY(record?.patientDoB)} label="Patient DOB" />
        <FunctionField label="Status" render={(record) => KitStatusLabels[record.status]} />
        <DateField
          label="Test Date"
          source="labTestedAt"
          emptyText="Not Tested Yet"
          options={{
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }}
        />
        <FunctionField label="Result" render={(record) => labTestResultLabelWithNotes(record)} />
        <FunctionField label="Report" render={(record) => ResultDetailPdf({ record, notify })} />
        <FunctionField label="Provider Ack'd" render={(record) => <StatusPill value={record.providerAcknowledgedAt > 0} />} />
        <ViewTestResultAction label="Action" />
      </DatagridConfigurable>
    </List>
  )
}

export default KitsAssociatedToBulkOrders
